'use client';

import React, { useState } from 'react';
import {
  createPasskey,
  passkeyErrorMessage,
  passkeysSupported,
  signWithPasskey,
  type PasskeySetupOptions,
  type PasskeySignInOptions,
} from '@/lib/passkeyClient';

// Step 2 of sign-in for finance roles. The first time, the user sets up a passkey (fingerprint,
// face or PIN on this device; recommended) or an authenticator app, then saves recovery codes.
// Afterwards: the passkey, the app code, or a recovery code.

export type MfaChallenge = {
  ticket: string;
  setup?: { secret: string; otpauthUrl: string; passkey?: PasskeySetupOptions };
  passkey?: PasskeySignInOptions | null;
  totp?: boolean;
};

const grouped = (secret: string) => secret.match(/.{1,4}/g)?.join(' ') ?? secret;
const button = 'w-full py-3 rounded-xl text-white font-semibold disabled:opacity-50';
const BLUE = { background: 'var(--brand)' };

export default function MfaStep({
  challenge,
  onDone,
  onCancel,
}: {
  challenge: MfaChallenge;
  onDone: (role: string) => void;
  onCancel: () => void;
}) {
  const supported = passkeysSupported();
  const [mode, setMode] = useState<'passkey' | 'app' | 'recovery'>(
    challenge.setup
      ? supported
        ? 'passkey'
        : 'app'
      : challenge.passkey && supported
        ? 'passkey'
        : 'app'
  );
  const [code, setCode] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [recovery, setRecovery] = useState<{ codes: string[]; role: string } | null>(null);

  const send = async (payload: object) => {
    const res = await fetch('/api/auth/mfa', {
      method: 'POST',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify({ ticket: challenge.ticket, ...payload }),
    });
    const body = await res.json().catch(() => ({}));
    if (!res.ok) {
      setError(body.error ?? 'That did not work. Try again.');
      if (res.status === 401 && /expired/i.test(body.error ?? '')) setTimeout(onCancel, 1500);
      else if (res.status === 423) setTimeout(onCancel, 2500);
      return;
    }
    const role = body.user?.user_metadata?.role ?? '';
    if (body.recoveryCodes) setRecovery({ codes: body.recoveryCodes, role });
    else onDone(role);
  };

  const run = async (fn: () => Promise<void>) => {
    setBusy(true);
    setError('');
    try {
      await fn();
    } catch (e) {
      setError(passkeyErrorMessage(e));
    } finally {
      setBusy(false);
    }
  };

  const doPasskey = () =>
    run(async () => {
      if (challenge.setup?.passkey) {
        const made = await createPasskey(challenge.setup.passkey);
        await send({ passkey: made, passkeyName: navigator.platform || 'Passkey' });
      } else if (challenge.passkey) {
        await send({ passkey: await signWithPasskey(challenge.passkey) });
      }
    });
  const doCode = (e: React.FormEvent) => {
    e.preventDefault();
    void run(() => send({ code }));
  };

  if (recovery)
    return (
      <div className="space-y-4">
        <h2 className="text-2xl font-bold" style={{ color: 'var(--ink)' }}>
          Save your recovery codes
        </h2>
        <p className="text-sm text-gray-600">
          If you lose this device, each code lets you sign in once instead. Write them down and keep
          them somewhere safe, away from your phone. They will not be shown again.
        </p>
        <div className="grid grid-cols-2 gap-2 font-mono text-sm bg-gray-50 border rounded-xl p-4">
          {recovery.codes.map((c) => (
            <span key={c}>{c}</span>
          ))}
        </div>
        <button onClick={() => onDone(recovery.role)} className={button} style={BLUE}>
          I have saved them, continue
        </button>
      </div>
    );

  const switcher = (
    <div className="flex flex-wrap justify-between gap-2 text-xs">
      <button type="button" onClick={onCancel} className="text-gray-500 underline">
        Back
      </button>
      <div className="flex flex-wrap gap-3">
        {mode !== 'passkey' && supported && (challenge.setup?.passkey || challenge.passkey) && (
          <button
            type="button"
            onClick={() => setMode('passkey')}
            className="text-[var(--brand)] underline"
          >
            Use a passkey instead
          </button>
        )}
        {mode !== 'app' && (challenge.setup || challenge.totp) && (
          <button
            type="button"
            onClick={() => setMode('app')}
            className="text-[var(--brand)] underline"
          >
            {challenge.setup ? 'Use an authenticator app instead' : 'Use the app code'}
          </button>
        )}
        {!challenge.setup && mode !== 'recovery' && (
          <button
            type="button"
            onClick={() => setMode('recovery')}
            className="text-[var(--brand)] underline"
          >
            Lost your device? Use a recovery code
          </button>
        )}
      </div>
    </div>
  );

  const title = challenge.setup ? 'Protect your account' : 'Confirm it is you';

  if (mode === 'passkey')
    return (
      <div className="space-y-5">
        <div>
          <h2 className="text-2xl font-bold mb-1" style={{ color: 'var(--ink)' }}>
            {title}
          </h2>
          <p className="text-sm" style={{ color: 'hsl(40,6%,50%)' }}>
            {challenge.setup
              ? 'Your role handles money, so sign-in needs a second step. Set up a passkey: your device will ask for your fingerprint, face or screen lock. Nothing to type or remember.'
              : 'Use your passkey: your device will ask for your fingerprint, face or screen lock.'}
          </p>
        </div>
        <button onClick={doPasskey} disabled={busy} className={button} style={BLUE}>
          {busy ? 'Waiting for your device…' : challenge.setup ? 'Set up a passkey' : 'Use passkey'}
        </button>
        <p className="text-xs" style={{ color: 'hsl(40,6%,50%)' }}>
          On a computer without a fingerprint reader, choose “use a phone” in the window that opens
          and scan the code with your phone’s camera.
        </p>
        {error && <p className="text-sm text-red-600">{error}</p>}
        {switcher}
      </div>
    );

  return (
    <form onSubmit={doCode} className="space-y-5">
      <div>
        <h2 className="text-2xl font-bold mb-1" style={{ color: 'var(--ink)' }}>
          {mode === 'recovery'
            ? 'Use a recovery code'
            : challenge.setup
              ? 'Set up an authenticator app'
              : title}
        </h2>
        <p className="text-sm" style={{ color: 'hsl(40,6%,50%)' }}>
          {mode === 'recovery'
            ? 'Enter one of the recovery codes you saved when you set up your second step.'
            : challenge.setup
              ? 'Use Google Authenticator, Microsoft Authenticator or a similar app.'
              : 'Open your authenticator app and enter the 6-digit code for WENLSU SACCO.'}
        </p>
      </div>
      {challenge.setup && mode === 'app' && (
        <ol className="text-sm text-gray-700 space-y-3 list-decimal pl-5">
          <li>
            In the app, choose <b>Add account</b> then <b>Enter a setup key</b>, and type:
            <div className="mt-2 font-mono text-base tracking-wider bg-gray-50 border rounded-lg px-3 py-2 select-all break-all">
              {grouped(challenge.setup.secret)}
            </div>
            <span className="text-xs text-gray-500">
              Type: time-based. On the phone with the app you can{' '}
              <a href={challenge.setup.otpauthUrl} className="underline text-[var(--brand)]">
                open the app directly
              </a>
              .
            </span>
          </li>
          <li>Enter the 6-digit code the app shows.</li>
        </ol>
      )}
      <input
        autoFocus
        inputMode={mode === 'recovery' ? 'text' : 'numeric'}
        autoComplete="one-time-code"
        placeholder={mode === 'recovery' ? 'XXXXX-XXXXX' : '123456'}
        value={code}
        onChange={(e) => setCode(e.target.value)}
        className="w-full px-4 py-3 rounded-xl border text-lg tracking-widest text-center font-mono"
        style={{ borderColor: 'hsl(40,6%,85%)' }}
      />
      {error && <p className="text-sm text-red-600">{error}</p>}
      <button
        type="submit"
        disabled={busy || code.trim().length < 6}
        className={button}
        style={BLUE}
      >
        {busy ? 'Checking…' : challenge.setup ? 'Finish setup' : 'Sign in'}
      </button>
      {switcher}
    </form>
  );
}
